/**
 * Left-menu items for the partner and staff surfaces. The shell drawer is the one menu on every surface; partner and
 * admin screens own their own (surface-local, translated) labels and role rules, so they register the items here and
 * the drawer renders them. Registration is a no-op outside the shell (tests, public pages).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { IconName } from './nav';

export interface SurfaceNavItem {
  to: string;
  /** already translated by the surface */
  label: string;
  icon: IconName;
  /** section heading (already translated); consecutive items sharing it render under one heading */
  group?: string;
  testId?: string;
  /** one of the four phone tab-bar shortcuts */
  tab?: boolean;
  /** full page load (<a href>) instead of a client-side route change */
  document?: boolean;
}

interface Registry { items: readonly SurfaceNavItem[]; set(items: readonly SurfaceNavItem[]): void }
const Ctx = createContext<Registry | null>(null);

export function SurfaceNavProvider({ children }: { children: ReactNode }) {
  const [items, set] = useState<readonly SurfaceNavItem[]>([]);
  const value = useMemo(() => ({ items, set }), [items]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSurfaceNavItems = (): readonly SurfaceNavItem[] => useContext(Ctx)?.items ?? [];

/** Registers the surface's menu for as long as the caller is mounted; pass [] while the surface is gated. */
export function useRegisterSurfaceNav(items: readonly SurfaceNavItem[]): void {
  const reg = useContext(Ctx);
  const set = reg?.set;
  const key = JSON.stringify(items);
  useEffect(() => {
    set?.(JSON.parse(key) as SurfaceNavItem[]);
    return () => set?.([]);
  }, [set, key]);
}
